import type { ViewAsState } from "../types";
import api from "./apiClient";

const VIEW_AS_STORAGE_KEY = "view_as_org";
const VIEW_AS_SELF_HEAL_KEY = "view_as_self_heal_attempted";
export const VIEW_AS_ROLES = ["member", "owner", "analyst", "developer", "contributor", "support"];

export function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getViewAsState(): ViewAsState | null {
  try {
    const raw = window.sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ViewAsState | null;
    return parsed && typeof parsed.organizationId === "string" && parsed.organizationId
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function resetScopeSelection(): void {
  try {
    localStorage.setItem("selected_scope", JSON.stringify({ type: "all" }));
  } catch {
    // Scope falls back to its default resolution when storage is unavailable.
  }
}

export interface EnterViewAsInput {
  organizationId: string;
  organizationName?: string | null;
  role?: string;
  mode?: string;
}

export function enterViewAs({
  organizationId,
  organizationName,
  role,
  mode,
}: EnterViewAsInput): void {
  const storage = getSessionStorage();
  if (storage === null) return;
  storage.setItem(
    VIEW_AS_STORAGE_KEY,
    JSON.stringify({
      organizationId,
      organizationName: organizationName || organizationId,
      role: VIEW_AS_ROLES.includes(role as string) ? role : "member",
      mode: mode === "rw" ? "rw" : "ro",
    }),
  );
  storage.removeItem(VIEW_AS_SELF_HEAL_KEY);
  resetScopeSelection();
  window.location.replace("/");
}

export function exitViewAs(): void {
  const storage = getSessionStorage();
  if (storage !== null) {
    storage.removeItem(VIEW_AS_STORAGE_KEY);
    storage.removeItem(VIEW_AS_SELF_HEAL_KEY);
  }
  resetScopeSelection();
  window.location.replace("/");
}

export function selfHealStaleViewAsState(): void {
  const storage = getSessionStorage();
  if (storage === null || getViewAsState() === null) return;
  if (storage.getItem(VIEW_AS_SELF_HEAL_KEY) === "1") return;
  storage.setItem(VIEW_AS_SELF_HEAL_KEY, "1");
  storage.removeItem(VIEW_AS_STORAGE_KEY);
  resetScopeSelection();
  window.location.replace("/");
}

/** The headers that make a request act for the organization staff are viewing as. */
export function viewAsRequestHeaders(): Record<string, string> {
  const viewAs = getViewAsState();
  if (viewAs === null) return {};
  return {
    "X-View-As-Mode": viewAs.mode === "rw" ? "rw" : "ro",
    "X-View-As-Org": viewAs.organizationId,
    "X-View-As-Role": viewAs.role || "member",
  };
}

api.interceptors.request.use((config) => {
  for (const [name, value] of Object.entries(viewAsRequestHeaders())) {
    config.headers[name] = value;
  }
  return config;
});

export interface ViewAsReadOnlyInterceptorOptions {
  notify?: (text: string) => void;
}

export function installViewAsReadOnlyInterceptor({
  notify,
}: ViewAsReadOnlyInterceptorOptions): () => void {
  if (!api?.interceptors?.response || typeof api.interceptors.response.use !== "function") {
    return () => {};
  }
  const interceptorId = api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error?.response?.status === 423 &&
        error?.response?.data?.code === "VIEW_AS_READ_ONLY" &&
        typeof notify === "function"
      ) {
        notify(error.response.data.error);
      }
      return Promise.reject(error);
    },
  );
  return () => {
    if (typeof api.interceptors.response.eject === "function") {
      api.interceptors.response.eject(interceptorId);
    }
  };
}
